import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';

const AudioPlayerContext = createContext(null);

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
}

// Alias for convenience / clear semantic usage
export const useMusicPlayer = useAudioPlayer;

const PLAYBACK_RATES = [1, 1.25, 1.5, 2];

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(new Audio());
  const [currentMedia, setCurrentMedia] = useState(null); // { type: 'music' | 'podcast', ...data }
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('audioPlayerVolume') || localStorage.getItem('musicVolume');
    return saved ? parseFloat(saved) : 1;
  });
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Music queue state
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(-1);
  const [repeatMode, setRepeatMode] = useState('off'); // 'off' | 'all' | 'one'
  const [isShuffled, setIsShuffled] = useState(false);

  const handleEndedRef = useRef(null);

  // Setup audio event listeners
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => setProgress(audio.currentTime);
    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      audio.playbackRate = playbackRate;
    };
    const handleDurationChange = () => setDuration(audio.duration || 0);
    const handleEnded = () => handleEndedRef.current?.();
    const handleError = () => {
      setError('Failed to load audio');
      setIsLoading(false);
      setIsPlaying(false);
    };
    const handleCanPlay = () => {
      setIsLoading(false);
      audio.playbackRate = playbackRate;
    };
    const handleWaiting = () => setIsLoading(true);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [playbackRate]);

  // Sync volume
  useEffect(() => {
    audioRef.current.volume = isMuted ? 0 : volume;
    localStorage.setItem('audioPlayerVolume', volume.toString());
  }, [volume, isMuted]);

  // Sync playback rate to HTML5 audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Music playback actions
  const playTrack = useCallback((track, newQueue = null) => {
    setError(null);
    setIsLoading(true);

    if (newQueue) {
      setQueue(newQueue);
      const index = newQueue.findIndex((t) => t.id === track.id);
      setQueueIndex(index >= 0 ? index : 0);
    }

    const mediaObj = {
      ...track,
      type: 'music',
    };
    setCurrentMedia(mediaObj);

    const streamUrl = apiService.getMusicStreamUrl(track.id);
    audioRef.current.src = streamUrl;
    audioRef.current.playbackRate = 1;
    setPlaybackRate(1);

    audioRef.current
      .play()
      .then(() => setIsPlaying(true))
      .catch((err) => {
        console.error('Play track error:', err);
        setError('Failed to play audio');
        setIsPlaying(false);
        setIsLoading(false);
      });
  }, []);

  const playTrackAtIndex = useCallback(
    (index) => {
      if (index >= 0 && index < queue.length) {
        setQueueIndex(index);
        playTrack(queue[index]);
      }
    },
    [queue, playTrack]
  );

  const playAlbum = useCallback(
    (tracks, startIndex = 0) => {
      if (!tracks || tracks.length === 0) return;
      setQueue(tracks);
      setQueueIndex(startIndex);
      playTrack(tracks[startIndex], tracks);
    },
    [playTrack]
  );

  const playNext = useCallback(() => {
    if (queue.length === 0) return;

    let nextIndex;
    if (isShuffled) {
      nextIndex = Math.floor(Math.random() * queue.length);
    } else {
      nextIndex = queueIndex + 1;
      if (nextIndex >= queue.length) {
        nextIndex = repeatMode === 'all' ? 0 : queueIndex;
      }
    }

    if (nextIndex !== queueIndex || repeatMode === 'all') {
      playTrackAtIndex(nextIndex);
    }
  }, [queue.length, queueIndex, isShuffled, repeatMode, playTrackAtIndex]);

  const playPrevious = useCallback(() => {
    if (queue.length === 0) return;

    if (audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }

    let prevIndex = queueIndex - 1;
    if (prevIndex < 0) {
      prevIndex = repeatMode === 'all' ? queue.length - 1 : 0;
    }
    playTrackAtIndex(prevIndex);
  }, [queue.length, queueIndex, repeatMode, playTrackAtIndex]);

  // Podcast playback action
  const playPodcast = useCallback(
    (episode) => {
      setError(null);
      setIsLoading(true);

      const mediaObj = {
        ...episode,
        type: 'podcast',
      };
      setCurrentMedia(mediaObj);

      const audioUrl = episode.audioUrl || episode.audio_url || '';
      audioRef.current.src = audioUrl;
      audioRef.current.playbackRate = playbackRate;

      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error('Play podcast error:', err);
          setError('Failed to play podcast episode');
          setIsPlaying(false);
          setIsLoading(false);
        });
    },
    [playbackRate]
  );

  // General playback controls
  const togglePlayPause = useCallback(() => {
    if (!currentMedia) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(console.error);
    }
  }, [currentMedia, isPlaying]);

  const seek = useCallback((time) => {
    const clamped = Math.max(0, Math.min(audioRef.current.duration || Infinity, time));
    audioRef.current.currentTime = clamped;
    setProgress(clamped);
  }, []);

  const seekPercent = useCallback(
    (percent) => {
      const time = (percent / 100) * duration;
      seek(time);
    },
    [duration, seek]
  );

  const skipTime = useCallback(
    (seconds) => {
      const targetTime = audioRef.current.currentTime + seconds;
      const clamped = Math.max(0, Math.min(audioRef.current.duration || Infinity, targetTime));
      audioRef.current.currentTime = clamped;
      setProgress(clamped);
    },
    []
  );

  const restartMedia = useCallback(() => {
    audioRef.current.currentTime = 0;
    setProgress(0);
    if (!isPlaying) {
      audioRef.current.play().catch(console.error);
    }
  }, [isPlaying]);

  const cyclePlaybackRate = useCallback(() => {
    setPlaybackRate((prev) => {
      const currentIndex = PLAYBACK_RATES.indexOf(prev);
      const nextIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
      return PLAYBACK_RATES[nextIndex];
    });
  }, []);

  const setVolumeLevel = useCallback((newVolume) => {
    const clamped = Math.max(0, Math.min(1, newVolume));
    setVolume(clamped);
    if (clamped > 0) setIsMuted(false);
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const closePlayer = useCallback(() => {
    audioRef.current.pause();
    audioRef.current.src = '';
    setCurrentMedia(null);
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setError(null);
  }, []);

  // Track end handler
  const handleMediaEnd = useCallback(() => {
    if (currentMedia?.type === 'music') {
      if (repeatMode === 'one') {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      } else if (queueIndex < queue.length - 1) {
        playNext();
      } else if (repeatMode === 'all' && queue.length > 0) {
        playTrackAtIndex(0);
      } else {
        setIsPlaying(false);
      }
    } else {
      // Podcast finished
      setIsPlaying(false);
      setProgress(0);
    }
  }, [currentMedia?.type, repeatMode, queueIndex, queue.length, playNext, playTrackAtIndex]);

  useEffect(() => {
    handleEndedRef.current = handleMediaEnd;
  }, [handleMediaEnd]);

  // Queue actions
  const toggleRepeat = useCallback(() => {
    setRepeatMode((prev) => {
      if (prev === 'off') return 'all';
      if (prev === 'all') return 'one';
      return 'off';
    });
  }, []);

  const toggleShuffle = useCallback(() => {
    setIsShuffled((prev) => !prev);
  }, []);

  const addToQueue = useCallback(
    (track) => {
      setQueue((prev) => {
        const newQueue = [...prev, track];
        if (!currentMedia) {
          const index = newQueue.length - 1;
          setQueueIndex(index);
          playTrack(track, newQueue);
        }
        return newQueue;
      });
    },
    [currentMedia, playTrack]
  );

  const removeFromQueue = useCallback(
    (index) => {
      setQueue((prev) => prev.filter((_, i) => i !== index));
      if (index < queueIndex) {
        setQueueIndex((prev) => prev - 1);
      } else if (index === queueIndex && queue.length > 1) {
        if (index < queue.length - 1) {
          playTrackAtIndex(index);
        } else {
          playTrackAtIndex(index - 1);
        }
      }
    },
    [queueIndex, queue.length, playTrackAtIndex]
  );

  const clearQueue = useCallback(() => {
    setQueue([]);
    setQueueIndex(-1);
    if (currentMedia?.type === 'music') {
      closePlayer();
    }
  }, [currentMedia?.type, closePlayer]);

  // Current track / episode accessors for convenience
  const currentTrack = currentMedia?.type === 'music' ? currentMedia : null;
  const currentEpisode = currentMedia?.type === 'podcast' ? currentMedia : null;

  const value = {
    // Media State
    currentMedia,
    currentTrack,
    currentEpisode,
    mediaType: currentMedia?.type || null,
    isPlaying,
    progress,
    duration,
    volume,
    isMuted,
    playbackRate,
    isLoading,
    error,

    // Music Queue State
    queue,
    queueIndex,
    repeatMode,
    isShuffled,

    // Playback Controls
    playTrack,
    playPodcast,
    playAlbum,
    togglePlayPause,
    seek,
    seekPercent,
    skipTime,
    restartMedia,
    setPlaybackRate,
    cyclePlaybackRate,
    setVolumeLevel,
    toggleMute,
    closePlayer,

    // Queue Controls
    playNext,
    playPrevious,
    toggleRepeat,
    toggleShuffle,
    addToQueue,
    removeFromQueue,
    clearQueue,
    playTrackAtIndex,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export default AudioPlayerProvider;
